import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const LegalModal = ({ isOpen, onClose, type }) => {
  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.3, ease: 'easeOut' },
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      transition: { duration: 0.2 },
    },
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 },
  };

  const getContent = () => {
    switch (type) {
      case 'terms':
        return {
          title: 'Terms and Conditions',
          content: (
            <div className="space-y-6">
              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  1. Acceptance of Terms
                </h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  By accessing and using DeviceLink Global's IMEI verification
                  services, you agree to be bound by these Terms and Conditions.
                  If you do not agree to these terms, please do not use our
                  services.
                </p>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  2. Service Description
                </h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
                  DeviceLink Global provides IMEI verification and device
                  information services including:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1 ml-4">
                  <li>Device identification and specifications</li>
                  <li>Carrier unlock status verification</li>
                  <li>Blacklist database checks</li>
                  <li>Device history reports</li>
                  <li>API access for business integration</li>
                </ul>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  3. User Responsibilities
                </h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
                  Users are responsible for:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1 ml-4">
                  <li>Providing accurate IMEI numbers for verification</li>
                  <li>Using services for legitimate purposes only</li>
                  <li>Maintaining account security and confidentiality</li>
                  <li>Complying with applicable laws and regulations</li>
                </ul>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  4. Payment and Billing
                </h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  Subscription fees are billed monthly in advance. All payments
                  are non-refundable except as required by law. We reserve the
                  right to modify pricing with 30 days notice to existing
                  subscribers.
                </p>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  5. Data Accuracy
                </h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  While we strive for accuracy, DeviceLink Global does not
                  guarantee the completeness or accuracy of all data.
                  Information is provided "as is" and users should verify
                  critical information independently.
                </p>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  6. Limitation of Liability
                </h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  DeviceLink Global shall not be liable for any indirect,
                  incidental, special, or consequential damages arising from the
                  use of our services. Our total liability is limited to the
                  amount paid for services.
                </p>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  7. Termination
                </h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  Either party may terminate the service agreement at any time.
                  Upon termination, access to services will be discontinued, but
                  these terms will survive termination where applicable.
                </p>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  8. Contact Information
                </h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  For questions about these terms, please contact us at
                  support@devicelinkglobal.com or through our contact form.
                </p>
              </section>
            </div>
          ),
        };

      case 'privacy':
        return {
          title: 'Privacy Policy',
          content: (
            <div className="space-y-6">
              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  1. Information We Collect
                </h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
                  We collect information you provide directly to us, such as:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1 ml-4">
                  <li>
                    Account registration information (name, email, country)
                  </li>
                  <li>IMEI numbers submitted for verification</li>
                  <li>Payment and billing information</li>
                  <li>Communication preferences and support requests</li>
                </ul>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  2. How We Use Your Information
                </h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
                  Your information is used to:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1 ml-4">
                  <li>Provide IMEI verification services</li>
                  <li>Process payments and manage subscriptions</li>
                  <li>Send service updates and support communications</li>
                  <li>Improve our services and user experience</li>
                  <li>Comply with legal obligations</li>
                </ul>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  3. Data Security
                </h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  We implement industry-standard security measures to protect
                  your personal information, including encryption, secure
                  servers, and regular security audits. However, no method of
                  transmission over the internet is 100% secure.
                </p>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  4. Information Sharing
                </h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  We do not sell, trade, or rent your personal information to
                  third parties. We may share information only when required by
                  law, to protect our rights, or with service providers who
                  assist in our operations under strict confidentiality
                  agreements.
                </p>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  5. Data Retention
                </h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  We retain your information for as long as necessary to provide
                  services and comply with legal obligations. IMEI verification
                  history may be retained for up to 2 years for service
                  improvement and fraud prevention.
                </p>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  6. Your Rights
                </h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
                  You have the right to:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1 ml-4">
                  <li>Access and update your personal information</li>
                  <li>
                    Request deletion of your data (subject to legal
                    requirements)
                  </li>
                  <li>Opt-out of marketing communications</li>
                  <li>Request data portability where applicable</li>
                </ul>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  7. Cookies and Tracking
                </h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  We use cookies and similar technologies to enhance user
                  experience, analyze usage patterns, and maintain user
                  sessions. You can control cookie preferences through your
                  browser settings.
                </p>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  8. Contact Us
                </h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  For privacy-related questions or to exercise your rights,
                  contact our Data Protection Officer at
                  privacy@devicelinkglobal.com or use our contact form.
                </p>
              </section>
            </div>
          ),
        };

      case 'about':
        return {
          title: 'About DeviceLink Global',
          content: (
            <div className="space-y-6">
              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  Our Mission
                </h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  DeviceLink Global is dedicated to providing the most
                  comprehensive and reliable IMEI verification services
                  worldwide. We empower businesses and individuals with accurate
                  device information to make informed decisions in the mobile
                  device ecosystem.
                </p>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  What We Do
                </h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
                  Our platform specializes in:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1 ml-4">
                  <li>Real-time IMEI verification and device identification</li>
                  <li>Comprehensive carrier unlock status checking</li>
                  <li>Global blacklist database access and monitoring</li>
                  <li>Device history and warranty status reports</li>
                  <li>Enterprise API solutions for business integration</li>
                  <li>Fraud prevention and device authentication services</li>
                </ul>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  Our Technology
                </h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  Built on cutting-edge infrastructure, our platform processes
                  over 600 verification requests per minute with 99.9% uptime
                  reliability. We maintain connections to global carrier
                  databases and continuously update our systems to provide the
                  most current device information available.
                </p>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  Global Reach
                </h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  With coverage spanning over 150 countries and partnerships
                  with major carriers worldwide, DeviceLink Global serves
                  customers across all continents. Our 24/7 support team ensures
                  assistance is always available, regardless of your timezone.
                </p>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  Our Commitment
                </h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
                  We are committed to:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1 ml-4">
                  <li>
                    Maintaining the highest standards of data accuracy and
                    security
                  </li>
                  <li>Providing transparent and competitive pricing</li>
                  <li>Continuously innovating our services and technology</li>
                  <li>Supporting our customers with exceptional service</li>
                  <li>
                    Operating with integrity and ethical business practices
                  </li>
                </ul>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  Industry Leadership
                </h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  Since our founding, DeviceLink Global has processed over 2.5
                  million device verifications and has become a trusted partner
                  for mobile retailers, insurance companies, law enforcement
                  agencies, and individual consumers worldwide.
                </p>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  Contact Information
                </h3>
                <div className="text-gray-700 dark:text-gray-300 leading-relaxed space-y-2">
                  <p>
                    <strong>Headquarters:</strong> Ireland, Wexford, Main Street
                  </p>
                  <p>
                    <strong>Email:</strong> support@devicelinkglobal.com
                  </p>
                  <p>
                    <strong>Business Hours:</strong> 24/7 Global Support
                  </p>
                  <p>
                    <strong>Emergency Support:</strong> Available via WhatsApp
                    and Skype
                  </p>
                </div>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  Join Our Community
                </h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  Connect with us on social media to stay updated on the latest
                  features, industry news, and company updates. We value
                  feedback from our community and continuously work to improve
                  our services based on user input.
                </p>
              </section>
            </div>
          ),
        };

      default:
        return { title: '', content: null };
    }
  };

  const { title, content } = getContent();

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-6">
        {/* Backdrop */}
        <motion.div
          className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          className="relative w-full max-w-6xl max-h-[95vh] bg-white dark:bg-gray-900 rounded-lg sm:rounded-2xl shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-700"
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-8 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
              {title}
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div
            className="p-4 sm:p-8 overflow-y-auto"
            style={{ maxHeight: 'calc(95vh - 100px)' }}
          >
            <div className="prose prose-gray dark:prose-invert max-w-none">
              {content}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default LegalModal;
